package com.appsyde.shared.presentation.style.style

import com.appsyde.shared.presentation.style.animations.AppStylesAnimations
import com.appsyde.shared.presentation.style.token.AppStylesTokens
import org.jetbrains.compose.web.css.*

object AppStylesWizard : StyleSheet() {

    /** Card wizard : colonne centrale, flex column. */
    val wizardCard by style {
        backgroundColor(Color(AppStylesTokens.CARD))
        borderRadius(AppStylesTokens.RADIUS_PAGE.px)
        property("border", "1px solid ${AppStylesTokens.BORDER}")
        property("box-shadow", AppStylesTokens.SHADOW_PAGE_CARD)
        property("transition", "box-shadow 160ms ease")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        minHeight(0.px)
        property("max-height", "calc(100vh - 100px)")
        overflow("hidden")
    }

    /** Contenu de la card : même animation (fade + translateY) à chaque changement d’étape. */
    val wizardCardInner by style {
        property("flex", "1")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        minHeight(0.px)
        overflow("hidden")
        property("animation", "${AppStylesAnimations.actionReveal.name} 0.35s ease-out forwards")
    }

    /** Header Hero : position relative pour statut en haut à droite. */
    val wizardCardHeader by style {
        padding(28.px, 32.px)
        paddingBottom(24.px)
        property("border-bottom", "1px solid rgba(15,23,42,0.06)")
        flexShrink(0)
        position(Position.Relative)
    }

    /** Micro ligne statutaire en haut à droite : ✔ Boîte connectée. */
    val wizardStatusLine by style {
        position(Position.Absolute)
        top(24.px)
        right(32.px)
        fontSize(12.px)
        fontWeight(500)
        color(Color(AppStylesTokens.BRAND))
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(6.px)
    }

    /** Icône hero optionnelle (subtil). */
    val wizardHeroIcon by style {
        fontSize(28.px)
        marginBottom(4.px)
        property("opacity", "0.9")
    }

    /** Titre hero : plus grand, assumé (28–32px, 700, letter-spacing). */
    val wizardTitle by style {
        fontSize(30.px)
        fontWeight(700)
        color(Color(AppStylesTokens.INK))
        property("letter-spacing", "-0.03em")
        marginBottom(12.px)
        property("line-height", "1.2")
    }

    /** Sous-texte plus léger, interligne respirant, max-width limitée. */
    val wizardSubWrap by style {
        maxWidth(420.px)
        marginBottom(28.px)
    }

    val wizardSub by style {
        fontSize(15.px)
        fontWeight(400)
        color(Color("rgba(15,23,42,0.52)"))
        lineHeight("1.6")
    }

    /** Espace vertical généreux avant le stepper. */
    val wizardStepperWrap by style {
        marginTop(8.px)
    }

    /** Stepper minimal : Configuration — Terminé, barre fine. */
    val wizardStepper by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(12.px)
    }

    val wizardStepperDot by style {
        width(8.px)
        height(8.px)
        borderRadius(50.percent)
        backgroundColor(Color("rgba(15,23,42,0.12)"))
        property("transition", "background-color 200ms ease, box-shadow 200ms ease")
    }

    val wizardStepperDotActive by style {
        backgroundColor(Color(AppStylesTokens.BRAND))
        property("box-shadow", "0 0 0 3px rgba(102,96,230,0.2)")
    }

    val wizardStepperLine by style {
        property("flex", "1")
        height(1.5.px)
        backgroundColor(Color("rgba(15,23,42,0.08)"))
        property("min-width", "40px")
    }

    val wizardStepperLineProgress by style {
        display(DisplayStyle.Block)
        height(100.percent)
        width(0.percent)
        backgroundColor(Color("rgba(102,96,230,0.4)"))
        property("transition", "width 280ms ease")
    }

    val wizardStepperLabel by style {
        fontSize(11.px)
        fontWeight(500)
        color(Color("rgba(15,23,42,0.45)"))
    }

    /** Body scrollable : plus d'espace hero → form ; scrollbar quasi blanche. */
    val wizardCardBody by style {
        padding(36.px, 32.px)
        paddingTop(48.px)
        property("flex", "1")
        minHeight(0.px)
        overflowY("auto")
        property("scrollbar-width", "thin")
        property("scrollbar-color", "rgba(15,23,42,0.06) transparent")
    }

    /** Body étape « Vous êtes prêt » : centre le bloc verticalement. */
    val wizardCardBodyCentered by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        padding(0.px, 32.px)
    }

    /** Footer sticky, actions à droite. */
    val wizardCardFooter by style {
        padding(20.px, 32.px)
        property("border-top", "1px solid rgba(15,23,42,0.06)")
        backgroundColor(rgba(255, 255, 255, 0.9))
        property("backdrop-filter", "blur(8px)")
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        alignItems(AlignItems.Center)
        gap(12.px)
        flexShrink(0)
    }

    /** Footer centré (étape « Vous êtes prêt ») : CTA unique, plus proche du texte (-12px). */
    val wizardCardFooterCenter by style {
        padding(8.px, 32.px, 20.px, 32.px)
        property("border-top", "1px solid rgba(15,23,42,0.06)")
        backgroundColor(rgba(255, 255, 255, 0.9))
        property("backdrop-filter", "blur(8px)")
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        flexShrink(0)
    }

    /** Bloc centré « Vous êtes prêt » : moins d’air pour éviter l’effet flottant. */
    val wizardReadyBlock by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        property("text-align", "center")
        padding(32.px, 0.px)
        justifyContent(JustifyContent.Center)
    }

    /** Conteneur halo : cercle violet très léger derrière le check pour remplir l’espace. */
    val wizardReadyIconWrap by style {
        width(100.px)
        height(100.px)
        borderRadius(50.percent)
        backgroundColor(Color(AppStylesTokens.BRAND_SOFT))
        property("box-shadow", "0 0 0 1px rgba(102,96,230,0.08)")
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        marginBottom(28.px)
    }

    /** Icône validation (✔) : plus grande (68px), plus marquée, setup terminé. */
    val wizardReadyIcon by style {
        fontSize(68.px)
        lineHeight(1.px)
        fontWeight(600)
        color(Color(AppStylesTokens.BRAND))
        property("opacity", "1")
    }

    /** Titre fort, centré. */
    val wizardReadyTitle by style {
        fontSize(28.px)
        fontWeight(700)
        color(Color(AppStylesTokens.INK))
        property("letter-spacing", "-0.03em")
        marginBottom(12.px)
        property("line-height", "1.2")
    }

    /** Plus d'espace entre titre et texte sur l'écran succès. */
    val wizardReadyTitleSpacing by style {
        marginBottom(20.px)
    }

    /** Phrase courte, centrée. */
    val wizardReadySub by style {
        fontSize(15.px)
        fontWeight(400)
        color(Color("rgba(15,23,42,0.55)"))
        lineHeight("1.55")
        maxWidth(360.px)
        marginBottom(6.px)
    }

    /** Bloc body écran succès : moins de padding pour ancrer le contenu (pas de flottement). */
    val wizardReadyBody by style {
        padding(28.px, 24.px, 24.px, 24.px)
    }

    /** Texte écran succès : largeur max plus large. */
    val wizardReadySubWide by style {
        property("max-width", "100%")
    }

    /** Footer écran succès : plus d'air au-dessus du bouton (pas collé au divider). */
    val wizardReadyFooter by style {
        padding(36.px, 32.px, 20.px, 32.px)
    }

    /** Bloc des étapes validées : ✓ Gmail connecté, etc. */
    val wizardReadyChecklist by style {
        marginTop(20.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        gap(8.px)
    }

    /** Une ligne du rappel (✓ + label). */
    val wizardReadyCheckItem by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(8.px)
        fontSize(14.px)
        fontWeight(500)
        color(Color("rgba(15,23,42,0.7)"))
    }

    val wizardReadyCheckMark by style {
        color(Color(AppStylesTokens.BRAND))
        fontSize(16.px)
    }

    /** Bouton primary (Continuer) : plus large que Passer, filled, hover élévation. */
    val wizardBtnPrimary by style {
        padding(12.px, 28.px)
        borderRadius(12.px)
        border(0.px)
        backgroundColor(Color(AppStylesTokens.BRAND))
        color(Color.white)
        fontSize(14.px)
        fontWeight(600)
        property("cursor", "pointer")
        property("transition", "transform 120ms ease, box-shadow 160ms ease, background-color 160ms ease")
        property("box-shadow", AppStylesTokens.SHADOW_BTN_PRIMARY)
        self + hover style {
            property("transform", "translateY(-1px)")
            property("box-shadow", AppStylesTokens.SHADOW_BTN_PRIMARY_HOVER)
        }
        self + active style { property("transform", "scale(0.98)") }
    }

    /** CTA final « Aller à la boîte » : plus large, plus proche du texte, même hauteur. */
    val wizardBtnPrimaryWide by style {
        padding(12.px, 44.px)
        borderRadius(12.px)
        border(0.px)
        backgroundColor(Color(AppStylesTokens.BRAND))
        color(Color.white)
        fontSize(14.px)
        fontWeight(600)
        property("cursor", "pointer")
        property("transition", "transform 120ms ease, box-shadow 160ms ease, background-color 160ms ease")
        property("box-shadow", AppStylesTokens.SHADOW_BTN_PRIMARY)
        self + hover style {
            property("transform", "translateY(-1px)")
            property("box-shadow", AppStylesTokens.SHADOW_BTN_PRIMARY_HOVER)
        }
        self + active style { property("transform", "scale(0.98)") }
    }

    /** Bouton secondary (Passer) : ghost. */
    val wizardBtnSecondary by style {
        padding(12.px, 24.px)
        borderRadius(12.px)
        border(1.5.px, LineStyle.Solid, Color("rgba(15,23,42,0.12)"))
        backgroundColor(Color.transparent)
        color(Color("rgba(15,23,42,0.78)"))
        fontSize(14.px)
        fontWeight(600)
        property("cursor", "pointer")
        property("transition", "transform 120ms ease, background-color 140ms ease, border-color 140ms ease")
        self + hover style {
            backgroundColor(Color("rgba(15,23,42,0.04)"))
            property("transform", "translateY(-1px)")
        }
        self + active style { property("transform", "scale(0.98)") }
    }

    /** Subcard : spacing + hairlines, fond quasi inexistant, pas d’ombre. */
    val wizardSubcard by style {
        padding(16.px, 18.px)
        borderRadius(12.px)
        backgroundColor(Color("rgba(15,23,42,0.008)"))
        property("border", "1px solid rgba(15,23,42,0.04)")
        marginBottom(16.px)
    }

    val wizardSubcardTitle by style {
        fontSize(13.px)
        fontWeight(600)
        color(Color("rgba(15,23,42,0.8)"))
        marginBottom(12.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(8.px)
    }

    val wizardBadgeCount by style {
        padding(2.px, 8.px)
        borderRadius(999.px)
        backgroundColor(Color("rgba(102,96,230,0.12)"))
        color(Color(AppStylesTokens.BRAND))
        fontSize(12.px)
        fontWeight(600)
    }

    /** Section action : label + input full width + bouton à droite. */
    val wizardSectionAction by style {
        marginBottom(28.px)
    }

    val wizardLabel by style {
        fontSize(13.px)
        fontWeight(600)
        color(Color("rgba(15,23,42,0.8)"))
        marginBottom(8.px)
        display(DisplayStyle.Block)
    }

    val wizardInputRow by style {
        display(DisplayStyle.Flex)
        gap(12.px)
        alignItems(AlignItems.Center)
    }

    val wizardInput by style {
        property("flex", "1")
        minWidth(0.px)
        padding(12.px, 16.px)
        borderRadius(12.px)
        border(1.5.px, LineStyle.Solid, Color("rgba(15,23,42,0.12)"))
        fontSize(14.px)
        backgroundColor(Color.white)
        property("transition", "border-color 160ms ease, box-shadow 160ms ease")
        self + focus style {
            property("outline", "none")
            property("border-color", "rgba(102,96,230,0.4)")
            property("box-shadow", "0 0 0 3px rgba(102,96,230,0.12)")
        }
    }

    /** Bouton Envoyer : fort mais ne domine pas (hauteur/padding réduits, 500, violet moins saturé). */
    val wizardBtnSend by style {
        padding(10.px, 16.px)
        borderRadius(12.px)
        border(0.px)
        backgroundColor(Color("rgba(102,96,230,0.82)"))
        color(Color.white)
        fontSize(13.px)
        fontWeight(500)
        property("cursor", "pointer")
        property("transition", "transform 120ms ease, background-color 160ms ease")
        property("flex-shrink", "0")
        self + hover style {
            backgroundColor(Color("rgba(102,96,230,0.9)"))
            property("transform", "translateY(-1px)")
        }
        self + active style { property("transform", "scale(0.98)") }
    }

    val wizardMicrocopy by style {
        fontSize(12.px)
        color(Color("rgba(15,23,42,0.5)"))
        marginTop(8.px)
        lineHeight("1.4")
    }

    /** Row : email (left) + badge rôle + action (ghost danger). Plus fine, hover discret. */
    val wizardRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(12.px)
        padding(6.px, 0.px)
        property("border-bottom", "1px solid rgba(15,23,42,0.05)")
        property("transition", "background-color 150ms ease")
        borderRadius(6.px)
        self + hover style {
            backgroundColor(Color("rgba(102,96,230,0.02)"))
        }
    }

    /** Dernière row d’une section : pas de border-bottom. */
    val wizardRowLast by style {
        property("border-bottom", "none")
    }

    val wizardRowEmail by style {
        fontSize(14.px)
        color(Color("rgba(15,23,42,0.85)"))
        property("flex", "1")
        minWidth(0.px)
        property("word-break", "break-all")
    }

    /** Pills rôle : Title case côté composant. Léger, 12px, pas de letter-spacing. */
    val wizardPillRole by style {
        padding(4.px, 8.px)
        borderRadius(999.px)
        fontSize(12.px)
        fontWeight(500)
        backgroundColor(Color("rgba(15,23,42,0.06)"))
        color(Color("rgba(15,23,42,0.65)"))
        property("flex-shrink", "0")
    }

    val wizardPillOwner by style {
        backgroundColor(Color("rgba(102,96,230,0.09)"))
        color(Color(AppStylesTokens.BRAND))
    }

    /** Bouton ghost danger (Révoquer / Retirer) : neutre par défaut, rouge doux au hover. */
    val wizardBtnGhostDanger by style {
        padding(6.px, 12.px)
        borderRadius(8.px)
        border(0.px)
        backgroundColor(Color.transparent)
        color(Color("rgba(15,23,42,0.5)"))
        fontSize(12.px)
        fontWeight(500)
        property("cursor", "pointer")
        property("transition", "color 150ms ease, background-color 150ms ease")
        self + hover style {
            color(Color("rgba(185,28,28,0.85)"))
            backgroundColor(Color("rgba(220,38,38,0.06)"))
        }
        self + active style { property("transform", "scale(0.98)") }
    }

    /** Toast succès discret (violet). */
    val wizardToast by style {
        padding(10.px, 16.px)
        borderRadius(10.px)
        backgroundColor(Color("rgba(102,96,230,0.12)"))
        color(Color("rgba(102,96,230,0.95)"))
        fontSize(13.px)
        fontWeight(500)
        marginBottom(16.px)
        property("transition", "opacity 200ms ease")
    }

    val wizardToastError by style {
        backgroundColor(Color("rgba(220,38,38,0.08)"))
        color(Color("rgba(220,38,38,0.95)"))
    }

    /** Caption micro "Setup 1/2" optionnel en haut. */
    val wizardCaption by style {
        fontSize(11.px)
        color(Color("rgba(15,23,42,0.4)"))
    }

    /** Badge "Envoyée" à côté d'un email en attente. */
    val wizardBadgeSent by style {
        fontSize(11.px)
        fontWeight(500)
        color(Color(AppStylesTokens.BRAND))
        marginLeft(8.px)
    }
}
